/**
 * CityAutocomplete Component
 * Autocomplete dropdown for city selection using get_cities RPC
 * Supports geolocation-based city suggestions
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, ChevronDown, X, Check, MapPin, Locate, Loader2, BookmarkPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { logger } from "@/lib/services";

export interface City {
  id: string;
  country_code: string;
  name: string;
  region_name: string | null;
  popularity: number;
  is_other: boolean;
  is_active: boolean;
  sort_order: number;
  parent_id: string | null;
  distance_km?: number;
}

interface CityAutocompleteProps {
  value: string; // city name (display value)
  cityId?: string | null; // city UUID
  onSelect: (city: City | null) => void;
  placeholder?: string;
  countryCode?: string;
  className?: string;
  showIcon?: boolean;
  /** Profile city to show at top (from saved profile) */
  profileCity?: City | null;
  /** Show save to profile prompt after geolocation */
  showSavePrompt?: boolean;
  /** Callback when user wants to save city to profile */
  onSaveToProfile?: (city: City) => void;
}

type GeolocationState = "idle" | "loading" | "success" | "error" | "denied";

export function CityAutocomplete({
  value,
  cityId,
  onSelect,
  placeholder = "Выберите город",
  countryCode = "RU",
  className,
  showIcon = true,
  profileCity,
  showSavePrompt = false,
  onSaveToProfile,
}: CityAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cities, setCities] = useState<City[]>([]);
  const [nearbyCities, setNearbyCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [geoState, setGeoState] = useState<GeolocationState>("idle");
  const [geoSupported] = useState(() => "geolocation" in navigator);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch cities from RPC
  const fetchCities = useCallback(async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_cities", {
        p_country_code: countryCode,
        p_query: searchQuery || null,
        p_limit: 50,
        p_offset: 0,
        p_include_inactive: false,
        p_include_other: true,
        p_require_coords: true,
      });

      if (error) {
        logger.error("Error fetching cities", {
          context: { feature: "geo", function_name: "fetchCities" },
          error,
          error_code: "cities_fetch_failed",
          error_type: "unexpected",
        });
        setCities([]);
      } else {
        setCities((data as City[]) || []);
      }
    } catch (err) {
      logger.error("Error fetching cities", {
        context: { feature: "geo", function_name: "fetchCities" },
        error: err as Error,
        error_code: "cities_fetch_failed",
        error_type: "unexpected",
      });
      setCities([]);
    } finally {
      setIsLoading(false);
    }
  }, [countryCode]);

  // Fetch nearby cities based on coordinates
  const fetchNearbyCities = useCallback(async (lat: number, lng: number) => {
    try {
      const { data, error } = await supabase.rpc("get_cities_nearby", {
        p_lat: lat,
        p_lng: lng,
        p_country_code: countryCode,
        p_limit: 5,
        p_radius_km: 100,
      });

      if (error) {
        logger.error("Error fetching nearby cities", {
          context: { feature: "geo", function_name: "fetchNearbyCities" },
          error,
          error_code: "nearby_cities_fetch_failed",
          error_type: "unexpected",
        });
        return [];
      }
      return (data as City[]) || [];
    } catch (err) {
      logger.error("Error fetching nearby cities", {
        context: { feature: "geo", function_name: "fetchNearbyCities" },
        error: err as Error,
        error_code: "nearby_cities_fetch_failed",
        error_type: "unexpected",
      });
      return [];
    }
  }, [countryCode]);

  // Handle geolocation request
  const handleGetLocation = useCallback(async () => {
    if (!geoSupported) return;
    
    setGeoState("loading");
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const nearby = await fetchNearbyCities(latitude, longitude);
        
        if (nearby.length > 0) {
          setNearbyCities(nearby);
          setGeoState("success");
          const closestCity = nearby[0];
          // Auto-select the closest city
          onSelect(closestCity);
          setIsOpen(false);
          
          // Show save prompt if enabled and city differs from profile city
          if (showSavePrompt && onSaveToProfile && profileCity?.id !== closestCity.id) {
            toast(
              `Сохранить "${closestCity.name}" как ваш город?`,
              {
                action: {
                  label: 'Сохранить',
                  onClick: () => onSaveToProfile(closestCity),
                },
                duration: 8000,
              }
            );
          }
        } else {
          setGeoState("error");
          setNearbyCities([]);
        }
      },
      (error) => {
        logger.warn("Geolocation error", {
          context: { feature: "geo", function_name: "handleGetLocation" },
          data: { code: error.code, message: error.message },
        });
        if (error.code === error.PERMISSION_DENIED) {
          setGeoState("denied");
        } else {
          setGeoState("error");
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  }, [geoSupported, fetchNearbyCities, onSelect, showSavePrompt, onSaveToProfile, profileCity]);

  // Initial load + search debounce
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        fetchCities(query);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, query, fetchCities]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSelect = (city: City) => {
    onSelect(city);
    setIsOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
    setQuery("");
  };

  // Display value with region if available
  const displayValue = value;

  const getGeoButtonText = () => {
    switch (geoState) {
      case "loading":
        return "Определяем...";
      case "denied":
        return "Доступ запрещён";
      case "error":
        return "Не удалось определить";
      default:
        return "Определить автоматически";
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger button */}
      <button
        type="button"
        data-testid="city-autocomplete-trigger"
        onClick={handleOpen}
        className="w-full h-input rounded-input bg-bg-secondary border-0 text-left px-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-2 overflow-hidden">
          {showIcon && (
            <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0" />
          )}
          <span 
            className={cn(
              "truncate",
              displayValue ? "text-text-primary" : "text-text-tertiary"
            )}
          >
            {displayValue || placeholder}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {displayValue && (
            <div
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e as any); }}
              className="p-1 hover:bg-bg-tertiary rounded-full cursor-pointer"
              aria-label="Очистить"
            >
              <X className="w-4 h-4 text-text-tertiary" />
            </div>
          )}
          <ChevronDown
            className={cn(
              "w-5 h-5 text-text-tertiary transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-bg-primary rounded-input shadow-lg border border-border-tertiary z-50 overflow-hidden">
          {/* Geolocation button */}
          {geoSupported && (
            <button
              type="button"
              onClick={handleGetLocation}
              disabled={geoState === "loading"}
              className={cn(
                "w-full px-4 py-3 text-left text-sm flex items-center gap-3 border-b border-border-tertiary transition-colors",
                geoState === "loading" 
                  ? "bg-bg-secondary text-text-tertiary cursor-wait"
                  : geoState === "denied" || geoState === "error"
                    ? "bg-bg-secondary text-text-tertiary"
                    : "hover:bg-bg-accent-pink text-text-accent-pink"
              )}
            >
              {geoState === "loading" ? (
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
              ) : (
                <Locate className="w-4 h-4 flex-shrink-0" />
              )}
              <span>{getGeoButtonText()}</span>
            </button>
          )}

          {/* Profile city (saved city) - show first if exists */}
          {profileCity && !query && (
            <div className="border-b border-border-tertiary">
              <div className="px-4 py-2 text-xs text-text-tertiary uppercase tracking-wide bg-bg-secondary flex items-center gap-1">
                <BookmarkPlus className="w-3 h-3" />
                Ваш город
              </div>
              <button
                type="button"
                onClick={() => handleSelect(profileCity)}
                className={cn(
                  "w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors",
                  cityId === profileCity.id && "bg-bg-accent-pink"
                )}
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-text-primary font-medium">{profileCity.name}</span>
                    {profileCity.region_name && (
                      <span className="text-xs text-text-tertiary">{profileCity.region_name}</span>
                    )}
                  </div>
                </div>
                {cityId === profileCity.id && (
                  <Check className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                )}
              </button>
            </div>
          )}

          {/* Nearby cities (if found) */}
          {nearbyCities.length > 0 && !query && (
            <div className="border-b border-border-tertiary">
              <div className="px-4 py-2 text-xs text-text-tertiary uppercase tracking-wide bg-bg-secondary">
                Рядом с вами
              </div>
              {nearbyCities.filter(c => c.id !== profileCity?.id).map((city) => (
                <button
                  key={`nearby-${city.id}`}
                  type="button"
                  onClick={() => handleSelect(city)}
                  className={cn(
                    "w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors",
                    cityId === city.id && "bg-bg-accent-pink"
                  )}
                >
                  <div className="flex flex-col">
                    <span className="text-text-primary">{city.name}</span>
                    <span className="text-xs text-text-tertiary">
                      {city.region_name}
                      {city.distance_km !== undefined && ` • ${city.distance_km} км`}
                    </span>
                  </div>
                  {cityId === city.id && (
                    <Check className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Search input */}
          <div className="p-2 border-b border-border-tertiary">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                ref={inputRef}
                type="text"
                data-testid="city-autocomplete-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск города..."
                className="w-full h-10 pl-9 pr-4 rounded-input bg-bg-secondary border-0 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-text-accent-pink"
              />
            </div>
          </div>

          {/* Cities list */}
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-center text-sm text-text-tertiary">
                Загрузка...
              </div>
            ) : cities.length === 0 ? (
              <div className="px-4 py-3 text-center text-sm text-text-tertiary">
                {query ? "Ничего не найдено" : "Нет доступных городов"}
              </div>
            ) : (
              cities.map((city) => (
                <button
                  key={city.id}
                  type="button"
                  data-testid={`city-option-${city.id}`}
                  onClick={() => handleSelect(city)}
                  className={cn(
                    "w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors",
                    cityId === city.id && "bg-bg-accent-pink"
                  )}
                >
                  <div className="flex flex-col">
                    <span className="text-text-primary">{city.name}</span>
                    {city.region_name && (
                      <span className="text-xs text-text-tertiary">{city.region_name}</span>
                    )}
                  </div>
                  {cityId === city.id && (
                    <Check className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CityAutocomplete;
